import { Alert, Box, FormControlLabel, Radio, TextField } from '@mui/material';
import { useCallback, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PhoneNumberField } from '~/components/GenericForm/PhoneNumberField';
import { PasswordSubmitMethod } from '~/scenes/PeopleModule/components/EmployeeActions/ResetPassword/PasswordSubmissionMethodForm/data';
import { usePasswordSubmitMethods } from '~/scenes/PeopleModule/components/EmployeeActions/ResetPassword/PasswordSubmissionMethodForm/usePasswordSubmitMethods';

type MethodRadioProps<F extends FieldValues> = {
  control: Control<F>;
  name: Path<F>;
  label: string;
  method: PasswordSubmitMethod;
  onRevalidate: () => void;
};

const PasswordSubmitMethodRadioButton = <F extends FieldValues>(
  props: MethodRadioProps<F>,
) => {
  const { control, name, label, onRevalidate, method } = props;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => {
        return (
          <FormControlLabel
            control={
              <Radio
                checked={value === method}
                onChange={() => {
                  onChange(method);
                  onRevalidate();
                }}
              />
            }
            label={label}
          />
        );
      }}
    />
  );
};

type PropsType<F extends FieldValues> = {
  control: Control<F>;
  fieldNames: {
    method: Path<F>;
    workEmail: Path<F>;
    mobile: Path<F>;
    alternateEmail: Path<F>;
  };
  triggerRevalidate?: (paths: Path<F>[]) => void;
};

export const PasswordSubmissionMethodForm = <F extends FieldValues>(
  props: PropsType<F>,
) => {
  const { control, fieldNames, triggerRevalidate } = props;

  const { t } = useTranslation();

  const { getPasswordSubmitMethods } = usePasswordSubmitMethods();

  const { isSubmitted } = useFormState({ control });

  const workEmail = useWatch<F>({
    control,
    name: fieldNames.workEmail,
  });

  const { isMethodEnabled, isMethodsAvailable } = useMemo(
    () => getPasswordSubmitMethods(workEmail),
    [getPasswordSubmitMethods, workEmail],
  );

  const selectedMethod = useWatch<F>({
    control,
    name: fieldNames.method,
  });

  const handleRevalidate = useCallback(() => {
    if (!!triggerRevalidate && isSubmitted) {
      triggerRevalidate([
        fieldNames.workEmail,
        fieldNames.mobile,
        fieldNames.alternateEmail,
      ]);
    }
  }, [fieldNames, triggerRevalidate, isSubmitted]);

  const renderMethodRadioButton = useCallback(
    (method: PasswordSubmitMethod, label: string) => {
      return (
        <PasswordSubmitMethodRadioButton
          control={control}
          name={fieldNames.method}
          label={label}
          method={method}
          onRevalidate={handleRevalidate}
        />
      );
    },
    [control, fieldNames, handleRevalidate],
  );

  if (!isMethodsAvailable) {
    return <Alert severity={'error'}>{t('configurationError')}</Alert>;
  }

  return (
    <Box>
      {isMethodEnabled(PasswordSubmitMethod.WorkEmail) && (
        <>
          {renderMethodRadioButton(
            PasswordSubmitMethod.WorkEmail,
            t('laWorkEmail'),
          )}

          <Controller
            control={control}
            name={fieldNames.workEmail}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TextField
                  label={t('laWorkEmail')}
                  value={value}
                  onChange={onChange}
                  size={'small'}
                  fullWidth={true}
                  error={!!error}
                  helperText={error?.message}
                  required={selectedMethod === PasswordSubmitMethod.WorkEmail}
                  disabled={true}
                />
              );
            }}
          />
        </>
      )}

      {isMethodEnabled(PasswordSubmitMethod.SMS) && (
        <>
          {renderMethodRadioButton(
            PasswordSubmitMethod.SMS,
            t('laEnterMobile'),
          )}

          <Controller
            control={control}
            name={fieldNames.mobile}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <PhoneNumberField
                  label={t('laEnterMobile')}
                  value={value}
                  onChange={onChange}
                  size={'small'}
                  fullWidth={true}
                  error={!!error}
                  helperText={error?.message}
                  required={selectedMethod === PasswordSubmitMethod.SMS}
                  disabled={selectedMethod !== PasswordSubmitMethod.SMS}
                />
              );
            }}
          />
        </>
      )}
      {isMethodEnabled(PasswordSubmitMethod.AlternateEmail) && (
        <>
          {renderMethodRadioButton(
            PasswordSubmitMethod.AlternateEmail,
            t('laEmailAltEmail'),
          )}

          <Controller
            control={control}
            name={fieldNames.alternateEmail}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TextField
                  label={t('laEmailAltEmail')}
                  size={'small'}
                  value={value}
                  onChange={onChange}
                  fullWidth={true}
                  error={!!error}
                  helperText={error?.message}
                  required={
                    selectedMethod === PasswordSubmitMethod.AlternateEmail
                  }
                  disabled={
                    selectedMethod !== PasswordSubmitMethod.AlternateEmail
                  }
                />
              );
            }}
          />
        </>
      )}
    </Box>
  );
};
